import {EyeIcon, EyeSlashIcon} from '@heroicons/react/24/outline';
import {first, maxBy} from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';

import {recordEvent} from 'src/components/Analytics';
import Dropdown from 'src/components/Dropdown';
import {useTimezone} from 'src/components/TimezoneContext';
import {useTranslations} from 'src/components/TranslationsContext';
import VizTable from 'src/components/Viz/VizTable';
import WithStylePortalWrapper from 'src/components/WithStylePortalWrapper';
import {Site} from 'src/lib/api/project';
import {
  ApiForecastTimeSeries,
  ApiObservationTimeSeries,
  ForecastTimeSeriesQuery,
  ObservationTimeSeriesQuery,
} from 'src/lib/api/series';
import {formatDateShortFull} from 'src/lib/dates';
import {downloadVizData} from 'src/lib/downloadData';

import {chartDownloadFilename, useChartImageMenu} from './chartMenu';
import VizQueries from './VizQueries';

type Props = {
  title: string;
  site: Site;
  observationQueries: ObservationTimeSeriesQuery[];
  observationTimeSeriesList: ApiObservationTimeSeries[] | undefined;
  forecastQueries: ForecastTimeSeriesQuery[];
  forecastTimeSeriesList: ApiForecastTimeSeries[] | undefined;
  vizEl: HTMLDivElement | null;
  thresholdLinesVisible?: boolean;
  onToggleThresholdLines?: () => void;
};

export default function VizMoreOption({
  title,
  site,
  observationQueries,
  observationTimeSeriesList,
  forecastQueries,
  forecastTimeSeriesList,
  vizEl,
  thresholdLinesVisible,
  onToggleThresholdLines,
}: Props) {
  const {t} = useTranslations();
  const [tableWindow, setTableWindow] = React.useState<Window | null>(null);
  const [tableHtmlEl, setTableHtmlEl] = React.useState<HTMLElement | null>(null);

  const [isQueryVisible, setIsQueryVisible] = React.useState<boolean>(false);

  const {getDisplayTimeZone} = useTimezone();
  const displayTimeZone = getDisplayTimeZone(site.localTimeZone);

  // most ts will have the same aggregation options, so let's pull off the first
  const {timeAggregation, aggregationMethod, rateVolumeMode} =
    first(observationTimeSeriesList)?.query || first(forecastTimeSeriesList)?.query || {};

  // 'level' is already conveyed by the chart title, so skip it here to avoid duplication
  const rateVolumePart = rateVolumeMode && rateVolumeMode !== 'level' ? rateVolumeMode : undefined;
  const aggregationParts = [rateVolumePart, timeAggregation, aggregationMethod].filter(Boolean);
  const aggregationPart = aggregationParts.length ? ` (${aggregationParts.join(', ')})` : '';

  const filename = () => chartDownloadFilename(`${title}${aggregationPart} for ${site.name}`);

  /** Issue time of the displayed forecast, shown in the footer of image exports. */
  function issueTimeAnnotation(): string | undefined {
    const allForecasts = (forecastTimeSeriesList ?? []).flatMap((ts) => ts.forecasts);
    const latestForecast = maxBy(allForecasts, (f) => f.issueTime.getTime());
    if (!latestForecast) return undefined;
    const formattedTime = formatDateShortFull(latestForecast.issueTime, displayTimeZone);
    return allForecasts.length === 1
      ? t('hydroforecastIssueDate', {date: formattedTime})
      : t('latestDisplayedHydroforecastIssueDate', {date: formattedTime});
  }

  const imageMenu = useChartImageMenu({
    buildChart: () =>
      vizEl && {
        vizEl,
        title: `${title} ${t('at')} ${site.name}`,
        subtitle: rateVolumePart ? t(rateVolumePart) : undefined,
        annotation: issueTimeAnnotation(),
      },
    filename,
    title,
  });

  return (
    <>
      <Dropdown
        title={imageMenu.label}
        itemWidth={200}
        items={[
          {
            props: {
              hidden: !observationTimeSeriesList && !forecastTimeSeriesList,
              onClick: () => {
                if (observationTimeSeriesList || forecastTimeSeriesList) {
                  downloadVizData(
                    site,
                    observationTimeSeriesList,
                    forecastTimeSeriesList,
                    `${filename()}.csv`,
                    displayTimeZone
                  );
                  recordEvent('download_csv_clicked', {title});
                }
              },
            },
            text: t('downloadCSV'),
          },
          ...imageMenu.items,
          {
            props: {
              onClick: () => {
                if (tableWindow) {
                  tableWindow.focus();
                } else {
                  const newTableWindow = window.open('', '_blank', undefined);

                  if (newTableWindow) {
                    newTableWindow.addEventListener('unload', () => {
                      setTableWindow(null);
                      setTableHtmlEl(null);
                    });

                    const htmlElement = newTableWindow.document.querySelector('html')!;

                    while (htmlElement.firstChild) {
                      htmlElement.removeChild(htmlElement.firstChild);
                    }

                    setTableWindow(newTableWindow);
                    setTableHtmlEl(htmlElement);
                    recordEvent('open_table_clicked', {title});
                  }
                }
              },
            },
            text: t('openTable'),
          },
          {
            props: {
              hidden: !observationQueries.length && !forecastQueries.length,
              onClick: () => {
                setIsQueryVisible(true);
              },
            },
            text: t('viewAPIv2Queries'),
          },
          {
            props: {
              onClick: () => {
                window.open(
                  'https://hydroforecast.com/support/article/192-getting-to-know-the-model-inputs/',
                  '_blank'
                );
              },
            },
            text: t('inputsAndSources'),
          },
          ...(onToggleThresholdLines
            ? [
                {
                  props: {
                    onClick: onToggleThresholdLines,
                  },
                  text: thresholdLinesVisible ? t('hideThresholdLines') : t('showThresholdLines'),
                  divider: true,
                  icon: thresholdLinesVisible ? (
                    <EyeIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ),
                },
              ]
            : []),
        ]}
        props={{
          className: 'relative ml-4',
        }}
        link={true}
      />
      {tableHtmlEl &&
        observationTimeSeriesList &&
        forecastTimeSeriesList &&
        ReactDOM.createPortal(
          <WithStylePortalWrapper title={title}>
            <VizTable
              title={title}
              observationTimeSeriesList={observationTimeSeriesList}
              forecastTimeSeriesList={forecastTimeSeriesList}
              site={site}
            />
          </WithStylePortalWrapper>,
          tableHtmlEl
        )}

      <VizQueries
        title={title}
        isVisible={isQueryVisible}
        setIsVisible={setIsQueryVisible}
        forecastQueries={forecastQueries}
        observationQueries={observationQueries}
      />
    </>
  );
}
